import { useState, useCallback, useRef } from "react";
import { Search, SearchX, X, Plus } from "lucide-react";
import { ITeamPlayer } from "../../models/IMatch";
import { searchPlayers } from "../../api/players";
import { registerTeam } from "../../api/tournaments";
import { feeWindowStatus } from "../../utils/dates";
import { teamConfirmBlocked, teamSizeHint } from "../../utils/registrationMode";
import { Alert, Avatar, Button, Dialog, EmptyState, Field, IconButton, Input, Loader, SearchInput, Select, Tag } from "../../ui";
import "./styles.scss";

type SelectedPlayer = ITeamPlayer & {
  feeId?: string | null;
};

type RegisterTeamDialogProps = {
  tournamentCategoryId: string;
  fees: {
    id: string;
    amount: number;
    currency: string;
    description: string;
    isEnabled: boolean;
    perTeam: boolean;
    startDate?: string | null;
    endDate?: string | null;
  }[];
  maxPlayers: number;
  /** A category open to teams and individuals: a team has at most this many players… */
  teamSize?: number;
  /** …and at least this many (default: teamSize, i.e. exactly teamSize). */
  minSize?: number;
  isOpen: boolean;
  onConfirm: () => void;
  onClose: () => void;
  registrationGate?: {
    state: number; // 0 = Open, 1 = NotOpenYet, 2 = Closed
    reopensAt?: string | null;
    message?: string | null;
  } | null;
};

const RegisterTeamDialog = (props: RegisterTeamDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [teamName, setTeamName] = useState("");
  const [players, setPlayers] = useState<ITeamPlayer[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<SelectedPlayer[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Inline "create new player" form (name only)
  const [showNewPlayer, setShowNewPlayer] = useState(false);
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");

  // A fee whose window has ended is never offered. Admins may still book one
  // that hasn't opened yet, but a new player starts on the one open right now
  // when there is exactly one.
  const perPlayerFees = props.fees.filter(
    (f) => f.isEnabled && !f.perTeam && feeWindowStatus(f).kind !== "ended"
  );
  const openPerPlayerFees = perPlayerFees.filter((f) => feeWindowStatus(f).kind !== "upcoming");
  const defaultFeeId =
    perPlayerFees.length === 1 ? perPlayerFees[0].id : openPerPlayerFees.length === 1 ? openPerPlayerFees[0].id : null;
  const feeOptionLabel = (fee: (typeof perPlayerFees)[number]) => {
    const status = feeWindowStatus(fee);
    const windowNote = status.kind === "upcoming" ? ` (${status.label})` : "";
    return `${fee.description} – ${fee.amount} ${fee.currency}${windowNote}`;
  };
  const maxTeamSize = props.maxPlayers;
  const isFull = maxTeamSize > 0 && selectedPlayers.length >= maxTeamSize;
  const sizeBlocked = teamConfirmBlocked(selectedPlayers.length, props.teamSize, props.minSize);
  const gateClosed = props.registrationGate != null && props.registrationGate.state !== 0;

  const fetchPlayers = useCallback(async (search: string) => {
    if (!search.trim()) {
      setPlayers([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await searchPlayers(search);
      setPlayers(data.map((p: any) => ({ ...p, playerId: p.id })));
    } catch {
      setPlayers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedSearch = useCallback(
    (value: string) => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => fetchPlayers(value), 500);
    },
    [fetchPlayers]
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (value.trim() !== searchTerm.trim()) setPlayers([]);
    debouncedSearch(value);
  };

  // The kit Dialog plays the exit animation itself: reset the form and hand the close to the parent right away.
  const handleClose = () => {
    setSelectedPlayers([]);
    setSearchTerm("");
    setTeamName("");
    setPlayers([]);
    setErrorMessages([]);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    props.onClose();
  };

  const handleSelectPlayer = (player: ITeamPlayer) => {
    if (maxTeamSize > 0 && selectedPlayers.length >= maxTeamSize) {
      setErrorMessages([`Maximum team size is ${maxTeamSize} players`]);
      return;
    }
    setSelectedPlayers((prev) => [
      ...prev,
      { ...player, feeId: defaultFeeId },
    ]);
    if (errorMessages.length > 0) setErrorMessages([]);
  };

  const handleRemovePlayer = (playerId: string) => {
    setSelectedPlayers((prev) => prev.filter((p) => p.playerId !== playerId));
  };

  const handleFeeChange = (playerId: string, feeId: string) => {
    setSelectedPlayers((prev) =>
      prev.map((p) => (p.playerId === playerId ? { ...p, feeId: feeId || null } : p))
    );
    if (errorMessages.length > 0) setErrorMessages([]);
  };

  const visiblePlayers = players.filter(
    (p) => !selectedPlayers.some((s) => s.playerId === p.playerId)
  );

  const handleAddUnregisteredPlayer = () => {
    const name = `${newFirstName.trim()} ${newLastName.trim()}`.trim();
    if (!name) return;
    if (maxTeamSize > 0 && selectedPlayers.length >= maxTeamSize) {
      setErrorMessages([`Maximum team size is ${maxTeamSize} players`]);
      return;
    }
    const fakeId = `unregistered_${Date.now()}`;
    setSelectedPlayers((prev) => [
      ...prev,
      {
        id: fakeId,
        playerId: fakeId,
        firstName: newFirstName.trim(),
        lastName: newLastName.trim(),
        nationality: "",
        feeId: defaultFeeId,
        _isUnregistered: true,
        _playerName: name,
      } as any,
    ]);
    setNewFirstName("");
    setNewLastName("");
    setShowNewPlayer(false);
    if (errorMessages.length > 0) setErrorMessages([]);
  };

  const handleConfirm = async () => {
    const errors: string[] = [];
    if (!teamName.trim()) errors.push("Team name is required");
    if (perPlayerFees.length > 0 && selectedPlayers.length > 0 && selectedPlayers.some((p) => !p.feeId))
      errors.push("Please select a fee for each member");

    if (errors.length > 0) {
      setErrorMessages(errors);
      return;
    }

    setErrorMessages([]);
    setSubmitting(true);
    try {
      await registerTeam({
        name: teamName.trim(),
        tournamentCategoryMappingId: props.tournamentCategoryId,
        teamMembers: selectedPlayers.map((p: any) => ({
          playerId: p._isUnregistered ? undefined : p.id,
          playerName: p._isUnregistered ? p._playerName : undefined,
          feeId: p.feeId || undefined,
          consent: true,
        })),
      });
      props.onConfirm();
      handleClose();
    } catch (error: any) {
      // The axios response interceptor rejects with the already-unwrapped
      // response body, so the API error fields live directly on `error`.
      // Support the controller's ServiceResponse shape ({ errors, message },
      // camelCase) and the exception middleware's ({ ErrorMessage }, PascalCase).
      const body = error?.response?.data ?? error ?? {};
      const apiErrors = body.errors ?? body.Errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        setErrorMessages(apiErrors.map((e: any) => e?.message ?? e?.Message ?? String(e)));
      } else {
        setErrorMessages([
          body.message || body.Message || body.ErrorMessage || error?.message || "Failed to add team",
        ]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const playerMeta = (player: ITeamPlayer) =>
    `${player.nationality}${(player as any).gender ? ` · ${(player as any).gender}` : ""}`;

  return (
    <Dialog
      open={props.isOpen}
      onClose={handleClose}
      title="Add Team"
      placement="side"
      size="sm"
      busy={submitting}
      bodyClassName="register-team"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            loading={submitting}
            disabled={!teamName.trim() || gateClosed || sizeBlocked}
          >
            {submitting ? "Adding..." : "Add Team"}
          </Button>
        </>
      }
    >
      {errorMessages.length > 0 && (
        <Alert tone="danger">
          {errorMessages.map((msg, i) => (
            <p key={i}>{msg}</p>
          ))}
        </Alert>
      )}
      {props.registrationGate && props.registrationGate.state !== 0 && (
        <Alert tone="warn">
          <p>{props.registrationGate.message}</p>
        </Alert>
      )}

      <Field label="Team Name" required>
        {(id) => (
          <Input
            id={id}
            type="text"
            placeholder="Enter team name"
            value={teamName}
            onChange={(e) => {
              setTeamName(e.target.value);
              if (errorMessages.length > 0) setErrorMessages([]);
            }}
            autoFocus
          />
        )}
      </Field>

      <Field
        label="Members"
        hint={sizeBlocked && props.teamSize != null ? teamSizeHint(props.teamSize, props.minSize) : undefined}
        aside={
          maxTeamSize > 0 && (
            // A Both team is complete, not over the limit, once it's full.
            <Tag tone={isFull ? (props.teamSize != null ? "ok" : "danger") : "neutral"}>
              {selectedPlayers.length}/{maxTeamSize}
            </Tag>
          )
        }
      >
        {(id) => (
          <div className="register-team__members">
            <SearchInput
              id={id}
              block
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={handleSearch}
            />

            {selectedPlayers.length > 0 && (
              <ul className="register-team__selected">
                {selectedPlayers.map((player) => (
                  <li key={player.playerId} className="register-team__selected-item">
                    <div className="register-team__row">
                      <Avatar name={`${player.firstName} ${player.lastName || ""}`} size={32} />
                      <div className="register-team__info">
                        <span className="register-team__name">{player.firstName} {player.lastName || ""}</span>
                        <span className="register-team__meta">{playerMeta(player)}</span>
                      </div>
                      <IconButton
                        label="Remove player"
                        icon={<X />}
                        variant="danger"
                        size="sm"
                        onClick={() => handleRemovePlayer(player.playerId)}
                      />
                    </div>
                    {perPlayerFees.length > 1 && (
                      <Select
                        className="register-team__fee-select"
                        size="sm"
                        block
                        aria-label="Fee"
                        value={player.feeId || ""}
                        onChange={(e) => handleFeeChange(player.playerId, e.target.value)}
                      >
                        <option value="">Select fee...</option>
                        {perPlayerFees.map((fee) => (
                          <option key={fee.id} value={fee.id}>
                            {feeOptionLabel(fee)}
                          </option>
                        ))}
                      </Select>
                    )}
                    {perPlayerFees.length === 1 && (
                      <span className="register-team__fee">{perPlayerFees[0].description} – {perPlayerFees[0].amount} {perPlayerFees[0].currency}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <div className="register-team__results">
              {!searchTerm.trim() ? (
                <EmptyState compact icon={<Search />} title="Search for players to add as team members" />
              ) : loading ? (
                <Loader label="Searching..." minHeight={120} />
              ) : visiblePlayers.length === 0 ? (
                <EmptyState compact icon={<SearchX />} title="No players found" />
              ) : (
                <ul className="register-team__list">
                  {visiblePlayers.map((player) => (
                    <li key={player.playerId}>
                      {/* Still clickable when full: the click explains the team-size limit, as before */}
                      <button
                        type="button"
                        className={`register-team__pick${isFull ? " is-disabled" : ""}`}
                        aria-disabled={isFull || undefined}
                        onClick={() => handleSelectPlayer(player)}
                      >
                        <Avatar name={`${player.firstName} ${player.lastName || ""}`} size={32} />
                        <span className="register-team__info">
                          <span className="register-team__name">{player.firstName} {player.lastName || ""}</span>
                          <span className="register-team__meta">{playerMeta(player)}</span>
                        </span>
                        <Plus className="register-team__pick-icon" size={16} aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Create new player (non-registered) */}
            {!showNewPlayer ? (
              <Button variant="link" icon={<Plus />} onClick={() => setShowNewPlayer(true)}>
                Add non-registered player
              </Button>
            ) : (
              <div className="register-team__new-player">
                <Input
                  inputSize="sm"
                  placeholder="First name *"
                  value={newFirstName}
                  onChange={(e) => setNewFirstName(e.target.value)}
                  autoFocus
                />
                <Input
                  inputSize="sm"
                  placeholder="Last name"
                  value={newLastName}
                  onChange={(e) => setNewLastName(e.target.value)}
                />
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!newFirstName.trim()}
                  onClick={handleAddUnregisteredPlayer}
                >
                  Add
                </Button>
                <IconButton
                  label="Cancel"
                  icon={<X />}
                  size="sm"
                  onClick={() => { setShowNewPlayer(false); setNewFirstName(""); setNewLastName(""); }}
                />
              </div>
            )}
          </div>
        )}
      </Field>
    </Dialog>
  );
};

export default RegisterTeamDialog;
